/**
 * Admin All Profiles Section
 * View all user profiles with preference cards and data masking
 * 
 * Last Updated: March 18, 2026
 * Owner: Naomi (Super Admin)
 */
import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, Search, RefreshCw, Eye, Filter, Download,
  Utensils, Accessibility, Heart, Scissors, Sparkles,
  CheckCircle, XCircle, Shield, BarChart3
} from "lucide-react";

// Category icons and colors
const CATEGORY_CONFIG = {
  dietary: { icon: Utensils, color: "text-orange-500", bg: "bg-orange-50" },
  accessibility: { icon: Accessibility, color: "text-blue-500", bg: "bg-blue-50" },
  interests: { icon: Heart, color: "text-pink-500", bg: "bg-pink-50" },
  hair: { icon: Scissors, color: "text-purple-500", bg: "bg-purple-50" },
  skin: { icon: Sparkles, color: "text-rose-500", bg: "bg-rose-50" },
  audience: { icon: Users, color: "text-cyan-500", bg: "bg-cyan-50" }
};

export default function AdminAllProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterHasDefaults, setFilterHasDefaults] = useState("all");
  const [filterPilotTag, setFilterPilotTag] = useState("all");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileDetail, setProfileDetail] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const token = localStorage.getItem("session_token");
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  });

  // Fetch profiles
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/profiles?skip=${skip}&limit=${limit}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (filterHasDefaults !== "all") url += `&has_defaults=${filterHasDefaults === "yes"}`;
      if (filterPilotTag !== "all") url += `&pilot_tag=${filterPilotTag}`;

      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
        setTotal(data.total || 0);
      } else {
        toast.error("Failed to load profiles");
      }
    } catch (err) {
      console.error("Fetch profiles error:", err);
      toast.error("Error loading profiles");
    } finally {
      setLoading(false);
    }
  }, [skip, searchQuery, filterHasDefaults, filterPilotTag]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/profiles/analytics/preferences`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Fetch analytics error:", err);
    }
  }, []);

  // Fetch profile detail
  const fetchProfileDetail = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/profiles/${userId}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfileDetail(data);
      }
    } catch (err) {
      console.error("Fetch profile detail error:", err);
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchAnalytics();
  }, [fetchProfiles, fetchAnalytics]);

  // View profile detail
  const handleViewProfile = (profile) => {
    setSelectedProfile(profile);
    fetchProfileDetail(profile.user_id);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6" data-testid="admin-all-profiles">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Profiles</h2>
          <p className="text-gray-500 mt-1">View user profiles with preference cards</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchProfiles} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{analytics.total_users}</div>
                  <div className="text-sm text-gray-500">Total Users</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{analytics.users_with_preferences}</div>
                  <div className="text-sm text-gray-500">With Preferences</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{analytics.preference_coverage}%</div>
                  <div className="text-sm text-gray-500">Coverage</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{analytics.category_breakdown?.length || 0}</div>
                  <div className="text-sm text-gray-500">Active Categories</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="profile-search"
                />
              </div>
            </div>
            <Select value={filterHasDefaults} onValueChange={setFilterHasDefaults}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Has preferences" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="yes">With Preferences</SelectItem>
                <SelectItem value="no">Without Preferences</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPilotTag} onValueChange={setFilterPilotTag}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Pilot tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pilots</SelectItem>
                <SelectItem value="pilot_instructor">Instructor</SelectItem>
                <SelectItem value="pilot_beauty">Beauty</SelectItem>
                <SelectItem value="pilot_events">Events</SelectItem>
                <SelectItem value="pilot_hthf_event">HTHF</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Profiles Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Preferences</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : profiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-gray-500">
                    No profiles found
                  </TableCell>
                </TableRow>
              ) : (
                profiles.map((profile) => (
                  <TableRow key={profile.user_id} data-testid={`profile-${profile.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={profile.profile_image} />
                          <AvatarFallback>{getInitials(profile.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{profile.name || "Unnamed"}</div>
                          <div className="text-xs text-gray-500">{profile.user_id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">{profile.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {profile.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag.replace("pilot_", "").replace("role_", "")}
                          </Badge>
                        ))}
                        {profile.tags?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{profile.tags.length - 3}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {profile.has_defaults ? (
                        <div className="flex gap-1">
                          {profile.filled_categories?.map((cat) => {
                            const config = CATEGORY_CONFIG[cat];
                            if (!config) return null;
                            const Icon = config.icon;
                            return (
                              <div
                                key={cat}
                                className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center`}
                                title={cat}
                              >
                                <Icon className={`w-3 h-3 ${config.color}`} />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewProfile(profile)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        {total > limit && (
          <CardContent className="border-t py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Showing {skip + 1}-{Math.min(skip + limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Profile Detail Modal */}
      <Dialog open={!!selectedProfile} onOpenChange={() => setSelectedProfile(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={selectedProfile?.profile_image} />
                <AvatarFallback>{getInitials(selectedProfile?.name)}</AvatarFallback>
              </Avatar>
              <div>
                <div>{selectedProfile?.name || "Unnamed User"}</div>
                <div className="text-sm font-normal text-gray-500">{selectedProfile?.email}</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {profileDetail ? (
              <div className="space-y-6 pr-4">
                {/* Data Masking Notice */}
                {profileDetail.data_masking_applied && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      Some data is masked based on your role ({profileDetail.viewer_role})
                    </span>
                  </div>
                )}

                {/* Preference Card */}
                {profileDetail.defaults ? (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Preference Card</CardTitle>
                      <CardDescription>User's saved preferences from intake and RSVPs</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Dietary */}
                      {profileDetail.defaults.dietary && (
                        <div className="p-3 rounded-lg bg-orange-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Utensils className="w-4 h-4 text-orange-500" />
                            <span className="font-medium">Dietary</span>
                          </div>
                          {profileDetail.defaults.dietary.dietary_restrictions?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {profileDetail.defaults.dietary.dietary_restrictions.map((r, i) => (
                                <Badge key={i} variant="outline">{r}</Badge>
                              ))}
                            </div>
                          )}
                          {profileDetail.defaults.dietary.allergies?.length > 0 && (
                            <div className="text-sm text-gray-600">
                              Allergies: {profileDetail.defaults.dietary.allergies.join(", ")}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Accessibility */}
                      {profileDetail.defaults.accessibility && (
                        <div className="p-3 rounded-lg bg-blue-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Accessibility className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">Accessibility</span>
                          </div>
                          {profileDetail.defaults.accessibility.physical_needs?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {profileDetail.defaults.accessibility.physical_needs.map((n, i) => (
                                <Badge key={i} variant="outline">{n}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Interests */}
                      {profileDetail.defaults.interests?.length > 0 && (
                        <div className="p-3 rounded-lg bg-pink-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Heart className="w-4 h-4 text-pink-500" />
                            <span className="font-medium">Interests</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {profileDetail.defaults.interests.map((interest, i) => (
                              <Badge key={i} variant="secondary">{interest}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Hair */}
                      {profileDetail.defaults.hair && (
                        <div className="p-3 rounded-lg bg-purple-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Scissors className="w-4 h-4 text-purple-500" />
                            <span className="font-medium">Hair</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {profileDetail.defaults.hair.hair_type && (
                              <div>Type: {profileDetail.defaults.hair.hair_type}</div>
                            )}
                            {profileDetail.defaults.hair.hair_texture && (
                              <div>Texture: {profileDetail.defaults.hair.hair_texture}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-gray-500">
                      <XCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No preferences saved yet</p>
                    </CardContent>
                  </Card>
                )}

                {/* Recent RSVPs */}
                {profileDetail.rsvps?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Recent RSVPs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {profileDetail.rsvps.slice(0, 5).map((rsvp, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded bg-gray-50">
                            <div>
                              <div className="font-medium">{rsvp.event_name || rsvp.event_id}</div>
                              <div className="text-sm text-gray-500">{rsvp.event_date}</div>
                            </div>
                            <Badge variant={rsvp.status === "confirmed" ? "default" : "secondary"}>
                              {rsvp.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
